import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { PointType } from '../../../team-apis/otterdam/answers/shape-typings';
import { ClientFeedback } from '../../../team-apis/otterdam/feedback';
import { CVFeedbackPopoverService } from '../../feedback/services/cv-feedback-popover.service';
import { AbstractFeedbackDialogService } from './abstract-feedback-dialog.service';
import { ClientFeedbackCrudService } from './crud/client-feedback-crud.service';

@Injectable()
export class ClientFeedbackDialogService extends AbstractFeedbackDialogService<ClientFeedback> {
  protected override feedbackCrudService = inject(ClientFeedbackCrudService);
  private browserControlService = inject(BrowserControlService);
  private feedbackPopoverService = inject(CVFeedbackPopoverService);

  override setUpSubscriptions() {
    if (this.taskWorkspaceStore.state().feedbackType === 'client') {
      super.setUpSubscriptions();
      this.setUpSubscriptionToClientFeedbacks();
    }
  }

  override openDetailView(point?: PointType) {
    this.taskWorkspaceStore('feedbackPopover').assign({
      missingShapePoint: point,
      selectedFrameRange: [
        this.store.state().frameNumber,
        this.store.state().frameNumber,
      ],
    });
    this.feedbackPopoverService.openView();
  }

  openCreateFeedbackDialog(point: PointType) {
    this.feedbackListService.closeFeedbackList();
    this.feedbackListService.clearSelection();
    this.taskWorkspaceStore('feedbackPopover').assign({
      missingShapePoint: point,
      selectedFrameRange: [
        this.store.state().frameNumber,
        this.store.state().frameNumber,
      ],
    });
    this.feedbackPopoverService.openDialog();
    this.feedbackCrudService.setFeedbackCoordinates(point, this.isDialogOpen());
  }

  getSelectedFeedback$() {
    return this.feedbackListService
      .getSelectedId$()
      .pipe(
        switchMap((selectedId) =>
          this.taskWorkspaceStore('clientFeedback').$.pipe(
            map((clientFeedbackList) =>
              clientFeedbackList.find(({ id }) => id === selectedId),
            ),
          ),
        ),
      );
  }

  isCurrentUserTheIssueCreator(issueId: string) {
    const issue = this.taskWorkspaceStore
      .state()
      .clientFeedback.find(({ id }) => id === issueId);
    return issue?.createdBy === this.taskWorkspaceStore.state().currentUser.id;
  }

  override isEditable() {
    return this.taskWorkspaceStore.state().readOnly;
  }

  override closeDialog(selectedFeedbackId?: string) {
    if (!this.isDialogOpen()) {
      return;
    }

    if (isDefined(selectedFeedbackId)) {
      this.reopenFeedbackListWithSelection(selectedFeedbackId);
    } else {
      this.clearFeedbackForAllOutputs();
    }
    this.feedbackPopoverService.closePopover();
  }

  override closeView() {
    this.feedbackPopoverService.closePopover();
  }

  override openEditFeedbackDialog() {
    this.feedbackPopoverService.openDialog();
    const selectedFeedback = this.getSelectedFeedback();
    assert(selectedFeedback, 'Selected feedback is always defined during edit');
    this.taskWorkspaceStore('feedbackPopover').assign({
      missingShapePoint: selectedFeedback.location.coordinates,
      selectedFrameRange: [
        selectedFeedback.location.frame_number,
        selectedFeedback.location.frame_number,
      ],
    });
  }

  protected override getStartFrame(feedback: ClientFeedback) {
    return feedback.location.frame_number;
  }

  protected override modifyShapeSelection() {}

  protected override changeOutput(feedback: ClientFeedback) {
    this.feedbackListService.switchOutput(feedback.location.output_id);
  }

  private setUpSubscriptionToClientFeedbacks() {
    this.subscribeTo(
      this.taskWorkspaceStore('clientFeedback').$.pipe(
        filter((clientFeedbackList) => !!clientFeedbackList.length),
        take(1),
      ),
      () => {
        this.selectAndRevealClientFeedback();
      },
    );
  }

  private selectAndRevealClientFeedback() {
    const queryParams = new URLSearchParams(
      this.browserControlService.getQueryParameters(),
    );
    const issueId = queryParams.get('issueId');
    if (issueId) {
      const matchedIssue = this.feedbackCrudService.getFeedback(issueId);
      if (
        matchedIssue &&
        matchedIssue.location.output_id ===
          this.outputInfoStore.state().output.id
      ) {
        const matchedIssueOutputId = matchedIssue.location.output_id;
        // fix me if you change this code

        if (this.taskWorkspaceStore.state().fusions[matchedIssueOutputId]) {
          this.feedbackListService.setSelection(matchedIssue.id);
          this.openDetailView();
        }
      }
    }
  }
}
